import React from 'react';
import { motion } from 'framer-motion';

export default function CommunityBanner() {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8"
    >
      <div className="relative rounded-3xl p-8 md:p-10 flex flex-col items-start justify-center shadow-xl overflow-hidden transition-all duration-700 hover:shadow-2xl border border-white/10 group">
        {/* Background Image with Dark Blue / Purple Gradient Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-1000 group-hover:scale-105"
          style={{ backgroundImage: `url('/images/community_banner_bg.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1636]/85 via-[#1a2d6d]/65 to-[#121c42]/85" />
        <motion.div 
          animate={{ 
            scale: [1, 1.25, 1],
            opacity: [0.3, 0.55, 0.3],
            x: [0, 20, 0]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-indigo-400/30 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl pointer-events-none"
        />

        <motion.div 
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.25, 0.45, 0.25],
            y: [0, -25, 0]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          className="absolute -bottom-24 -left-24 w-96 h-96 bg-gradient-to-tr from-blue-500/20 via-cyan-400/20 to-indigo-600/30 rounded-full blur-3xl pointer-events-none"
        />

        {/* Ambient Shimmer Line */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out pointer-events-none" />

        {/* Heading */}
        <motion.h2 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-2xl md:text-4xl font-black text-white mb-3 tracking-tight z-10 drop-shadow-sm flex items-center gap-2"
        >
          <span>Join Our Social Media Community</span>
        </motion.h2>

        {/* Subtitle */}
        <motion.p 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-[#b3c5ff] text-sm md:text-base mb-7 z-10 font-medium max-w-2xl"
        >
          Get instant updates, ticket drops & connect with Lokanatya across social platforms.
        </motion.p>
        
        {/* Buttons List with Hover Motion */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-wrap items-center gap-3.5 z-10"
        >
          {/* WhatsApp Channel */}
          <motion.a 
            href="https://wa.me/918917493408"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.96 }}
            className="bg-white hover:bg-emerald-50 text-[#128C7E] hover:text-[#075E54] px-5 py-3 rounded-2xl font-black text-xs md:text-sm flex items-center justify-center transition-all shadow-md hover:shadow-emerald-500/25 border border-emerald-100"
          >
            <svg className="mr-2.5 shrink-0 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" width="18" height="18">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>WhatsApp Channel</span>
          </motion.a>

          {/* Instagram */}
          <motion.a 
            href="https://www.instagram.com/p/DOAzn6RE5Pl/"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.96 }}
            className="bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white text-xs md:text-sm font-bold py-3 px-5 rounded-2xl flex items-center shadow-md hover:shadow-pink-500/30 transition-all"
          >
            <svg className="mr-2.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" width="18" height="18">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
            </svg>
            <span>Follow on Instagram</span>
          </motion.a>

          {/* Facebook */}
          <motion.a 
            href="https://facebook.com"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.96 }}
            className="bg-[#1877f2] hover:bg-[#0c59c0] text-white text-xs md:text-sm font-bold py-3 px-5 rounded-2xl flex items-center shadow-md hover:shadow-blue-500/30 transition-all"
          >
            <svg className="mr-2.5 shrink-0" fill="currentColor" viewBox="0 0 24 24" width="18" height="18">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span>Facebook Page</span>
          </motion.a>

          {/* YouTube */}
          <motion.a 
            href="https://www.youtube.com/@Lokanatya"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.96 }}
            className="bg-[#ff0000] hover:bg-[#cc0000] text-white text-xs md:text-sm font-bold py-3 px-5 rounded-2xl flex items-center shadow-md hover:shadow-red-500/30 transition-all"
          >
            <svg className="mr-2.5 shrink-0" fill="currentColor" viewBox="0 0 24 24" width="18" height="18">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.5 12 3.5 12 3.5s-7.505 0-9.377.55a3.016 3.016 0 0 0-2.122 2.136C0 8.07 0 12 0 12s0 3.93.501 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.55 9.377.55 9.377.55s7.505 0 9.377-.55a3.016 3.016 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <span>YouTube Channel</span>
          </motion.a>
        </motion.div>
      </div>
    </motion.div>
  );
}
